import i18next from "i18next";
import moment from "moment";
import {
  PaymentStepDTO,
  PaymentStatusEnum,
} from "../../../../services/paymentModule";

export interface PaymentHistoryGroup {
  planCode: string;
  planDescription: string;
  payment_type?: string;
  paymentSteps: PaymentStepDTO[];
}

export interface CancelSubscriptionInfo {
  uniqueLink: string;
  description: string;
  amount: number;
  currency: string;
  paymentType: string;
}

export type InvoiceLinkVariant = "receipt" | "payment";

export type PlanKind = "recurring" | "oneOff" | "free";

export const planKindOf = (paymentType?: string): PlanKind => {
  if (paymentType === "UNIQUE") return "oneOff";
  if (paymentType === "FREE") return "free";
  return "recurring";
};

export const isRecurringPlan = (paymentType?: string): boolean =>
  !!paymentType && planKindOf(paymentType) === "recurring";

export const byDateDesc = (a: string, b: string) =>
  moment(b).valueOf() - moment(a).valueOf();

export const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(i18next.language || "en", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount / 100);
  } catch (error) {
    return `${(amount / 100).toFixed(2)} ${currency}`;
  }
};

export const getPaymentData = (payment: PaymentStepDTO) => {
  const detailMap = payment.paymentDetailMap;

  return {
    amount: detailMap?.price || 0,
    currency: detailMap?.price_currency || "EUR",
    paymentId: detailMap?.payment_id || "N/A",
    paymentProviderIdentification: detailMap?.default_payment_method || null,
    invoiceIdentification: detailMap?.invoice_id || null,
    providerStatus: detailMap?.provider_status || null,
    amountRefunded: payment.amount_refunded || 0,
    refundReason: payment.refund_reason || "",
  };
};

export type PaymentData = ReturnType<typeof getPaymentData>;

export interface PlanRefund {
  payment: PaymentStepDTO;
  paymentData: PaymentData;
}

export interface PlanEntry {
  payment: PaymentStepDTO;
  paymentData: PaymentData;
  refunds: PlanRefund[];
  refundedTotal: number;
}

export interface ActivityEntry extends PlanEntry {
  planCode: string;
  planDescription: string;
  planPaymentType: string;
}

export const buildPlanEntries = (group: PaymentHistoryGroup): PlanEntry[] => {
  const steps = [...group.paymentSteps].sort((a, b) =>
    byDateDesc(a.paymentDateTime, b.paymentDateTime)
  );
  const refunds = steps.filter((s) => s.paymentStatusEnum === "REFUNDED");
  const charges = steps.filter((s) => s.paymentStatusEnum !== "REFUNDED");
  const attached = new Set<string>();

  const chargeEntries: PlanEntry[] = charges.map((payment) => {
    const own = payment.paymentRequestUniqueLink
      ? refunds.filter(
          (r) =>
            !attached.has(r.paymentStepIdentification) &&
            r.paymentRequestUniqueLink === payment.paymentRequestUniqueLink &&
            moment(r.paymentDateTime).isSameOrAfter(
              moment(payment.paymentDateTime)
            )
        )
      : [];
    own.forEach((r) => attached.add(r.paymentStepIdentification));

    const ownData: PlanRefund[] = own.map((r) => ({
      payment: r,
      paymentData: getPaymentData(r),
    }));

    return {
      payment,
      paymentData: getPaymentData(payment),
      refunds: ownData,
      refundedTotal: ownData.reduce(
        (sum, r) =>
          sum + (r.paymentData.amountRefunded || r.paymentData.amount),
        0
      ),
    };
  });

  const orphanRefunds: PlanEntry[] = refunds
    .filter((r) => !attached.has(r.paymentStepIdentification))
    .map((payment) => ({
      payment,
      paymentData: getPaymentData(payment),
      refunds: [],
      refundedTotal: 0,
    }));

  return [...chargeEntries, ...orphanRefunds].sort((a, b) =>
    byDateDesc(a.payment.paymentDateTime, b.payment.paymentDateTime)
  );
};

export const buildActivityStream = (
  paymentHistory: PaymentHistoryGroup[]
): ActivityEntry[] =>
  paymentHistory
    .flatMap((group) =>
      buildPlanEntries(group).map((entry) => ({
        ...entry,
        planCode: group.planCode,
        planDescription: group.planDescription,
        planPaymentType: group.payment_type || "",
      }))
    )
    .sort((a, b) =>
      byDateDesc(a.payment.paymentDateTime, b.payment.paymentDateTime)
    );

export type SubscriptionState =
  | "active"
  | "paymentFailed"
  | "suspended"
  | "canceled"
  | "ended";

export interface PlanSummary {
  planCode: string;
  planDescription: string;
  paymentType: string;
  kind: PlanKind;
  state: SubscriptionState | null;
  providerStatus: string | null;
  providerIsActive: boolean;
  amount: number;
  currency: string;
  latestStepDate: string;
  nextDate: string | null;
  movements: number;
}

const isProviderActive = (providerStatus: string | null) =>
  (providerStatus || "").toUpperCase() === "ACTIVE";

const resolveState = (
  status: PaymentStatusEnum,
  providerActive: boolean
): SubscriptionState | null => {
  switch (status) {
    case "ACTIVE":
      return "active";
    case "UNPAID":
    case "ERROR":
      return providerActive ? "paymentFailed" : "suspended";
    case "CANCELED":
      return "canceled";
    case "ENDED":
      return providerActive ? "active" : "ended";
    default:
      return null;
  }
};

export type PeriodStatus = "running" | "lapsed" | "none" | "unknown";

export interface CurrentPeriod {
  status: PeriodStatus;
  start: string;
  end: string | null;
  days: number | null;
  amount: number;
  currency: string;
}

export const resolveCurrentPeriod = (
  group: PaymentHistoryGroup,
  now: moment.Moment = moment()
): CurrentPeriod | null => {
  if (!isRecurringPlan(group.payment_type)) return null;

  const latest = [...group.paymentSteps]
    .filter((s) => s.paymentStatusEnum !== "REFUNDED")
    .sort((a, b) => byDateDesc(a.paymentDateTime, b.paymentDateTime))[0];
  if (!latest) return null;

  const start = latest.paymentDateTime;
  const end = latest.paymentNextDate || null;
  const { amount, currency } = getPaymentData(latest);
  const base = { start, end, amount, currency };

  if (latest.paymentStatusEnum === "CANCELED") {
    return { ...base, status: "none", days: null };
  }
  if (!end) {
    return { ...base, status: "unknown", end: null, days: null };
  }

  const today = now.clone().startOf("day");
  const lastDay = moment(end).startOf("day");

  return today.isAfter(lastDay)
    ? { ...base, status: "lapsed", days: today.diff(lastDay, "days") }
    : { ...base, status: "running", days: lastDay.diff(today, "days") };
};

export const summarizePlans = (
  paymentHistory: PaymentHistoryGroup[]
): PlanSummary[] =>
  paymentHistory
    .filter((group) => group.paymentSteps.length > 0)
    .map((group) => {
      const latest = [...group.paymentSteps].sort((a, b) =>
        byDateDesc(a.paymentDateTime, b.paymentDateTime)
      )[0];
      const data = getPaymentData(latest);
      const providerActive = isProviderActive(data.providerStatus);
      const kind = planKindOf(group.payment_type);

      return {
        planCode: group.planCode,
        planDescription: group.planDescription,
        paymentType: group.payment_type || "",
        kind,
        state:
          kind === "recurring"
            ? resolveState(latest.paymentStatusEnum, providerActive)
            : null,
        providerStatus: data.providerStatus,
        providerIsActive: providerActive,
        amount: data.amount,
        currency: data.currency,
        latestStepDate: latest.paymentDateTime,
        nextDate: latest.paymentNextDate || null,
        movements: buildPlanEntries(group).length,
      };
    })
    .sort((a, b) => byDateDesc(a.latestStepDate, b.latestStepDate));
